/**
 * Small hand-drawn outline icon set for module tiles/nav. Deliberately not
 * a third-party icon library — this app has no UI dependency of any kind
 * (see index.css), and a handful of 24x24 outline glyphs isn't worth
 * adding one. Each icon uses stroke="currentColor" so it inherits
 * whatever color the wrapping element sets (e.g. .home-tile-icon's
 * `color: var(--accent)`) — no per-icon color prop needed.
 */
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const base = {
  viewBox: "0 0 24 24",
  fill: "none" as const,
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export function CarIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 16V11.5L6 7h12l2 4.5V16" />
      <path d="M4 16h16v2.5a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1V17H7v1.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V16Z" />
      <circle cx="7.5" cy="16" r="1.4" />
      <circle cx="16.5" cy="16" r="1.4" />
      <path d="M6 11.5h12" />
    </svg>
  );
}

export function BuildingIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="5" y="3.5" width="10" height="17" rx="1" />
      <path d="M15 9.5h4v11h-4" />
      <path d="M8 7.5h1M8 10.5h1M8 13.5h1M8 16.5h1M11.5 7.5h1M11.5 10.5h1M11.5 13.5h1M11.5 16.5h1" />
    </svg>
  );
}

export function QuoteIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M6 3.5h9l3 3v14H6z" />
      <path d="M15 3.5v3h3" />
      <path d="M8.5 12h7M8.5 15h7M8.5 9h4" />
    </svg>
  );
}

export function WrenchIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M14.7 6.3a3.5 3.5 0 0 0-4.6 4.3L4 16.7V20h3.3l6-6.1a3.5 3.5 0 0 0 4.3-4.6l-2.6 2.6-2-2z" />
    </svg>
  );
}

export function PersonIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 19.5c0-3.6 3.1-6 7-6s7 2.4 7 6" />
    </svg>
  );
}

export function ClockIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="8.3" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}

export function GaugeIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 15.5a8 8 0 1 1 16 0" />
      <path d="M12 15.5 15.2 10" />
      <path d="M4 15.5h1.5M18.5 15.5H20M6.5 8.5l1 1M17.5 8.5l-1 1" />
    </svg>
  );
}

/** KPI Dashboards — an office display screen. */
export function MonitorIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="3" y="4.5" width="18" height="12" rx="1.5" />
      <path d="M8.5 20h7M12 16.5V20" />
    </svg>
  );
}

/** Health & Safety — a shield with a tick, the standard mark for "checked and sound". */
export function ShieldCheckIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M12 3.5 19 6v6c0 4.2-2.9 7.3-7 8.5-4.1-1.2-7-4.3-7-8.5V6l7-2.5Z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </svg>
  );
}

/** A checklist: what the To Do section is, before you have read the word. */
export function ChecklistIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M9 6h10" />
      <path d="M9 12h10" />
      <path d="M9 18h10" />
      <path d="m3.5 5.5 1.4 1.4L7.5 4.3" />
      <path d="m3.5 11.5 1.4 1.4 2.6-2.6" />
      <circle cx="5" cy="18" r="1.6" />
    </svg>
  );
}

/** Sales — a rising line, the one thing every sales screen is about. */
export function TrendIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="m7.5 15 3.5-4 3 2.5L20 7.5" />
      <path d="M20 11V7.5h-3.5" />
    </svg>
  );
}
